import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NotebookPen, X, Lock, Check } from "lucide-react";
import { api } from "@/lib/api";

export default function NotesPanel() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    api.get("/admin/notes").then(({ data }) => { setText(data.text || ""); loaded.current = true; }).catch(() => {});
  }, []);

  // autosave (debounced) — private to the signed-in user
  useEffect(() => {
    if (!loaded.current) return;
    setStatus("Saving...");
    const t = setTimeout(() => {
      api.put("/admin/notes", { text })
        .then(() => setStatus("Saved"))
        .catch(() => setStatus("Save failed"));
    }, 800);
    return () => clearTimeout(t);
  }, [text]);

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open personal notes"
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[70] w-11 h-16 rounded-l-xl bg-[#1D6FF2] text-white shadow-lg shadow-blue-900/50 flex items-center justify-center hover:w-12 transition-all duration-200"
        data-testid="notes-toggle-button"
      >
        <NotebookPen size={19} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 h-full w-full sm:w-[380px] z-[75] bg-[#0D1F3C] border-l border-white/10 shadow-2xl flex flex-col"
            data-testid="notes-panel"
          >
            <div className="p-5 border-b border-white/10 flex items-start justify-between">
              <div>
                <h3 className="font-display font-extrabold text-white">Personal Notes</h3>
                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-400">
                  <Lock size={11} /> Private — only you can see this
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Close notes" data-testid="notes-close-button">
                <X size={17} />
              </button>
            </div>
            <textarea
              className="flex-1 bg-transparent text-slate-200 text-sm leading-relaxed p-5 outline-none resize-none placeholder:text-slate-500"
              placeholder="Jot down reminders, follow-ups, anything..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="notes-textarea"
            />
            <div className="p-4 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              {status === "Saved" && <Check size={13} className="text-emerald-400" />}
              {status || "Notes save automatically"}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
